<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from '@inertiajs/vue3'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import LocationPickerModal from '@/Components/UI/LocationPickerModal.vue'
import Icon from '@/Components/UI/Icon.vue'

const props = defineProps({
  open:   { type: Boolean, default: false },
  office: { type: Object,  default: null },
})
const emit = defineEmits(['close'])

const { t } = useI18n()

const form = useForm({
  name:    '',
  address: '',
  lat:     null,
  lng:     null,
})

watch(() => props.office, (o) => {
  form.name    = o?.name    ?? ''
  form.address = o?.address ?? ''
  form.lat     = o?.lat     ?? null
  form.lng     = o?.lng     ?? null
}, { immediate: true })

const pickerOpen = ref(false)

// LocationPickerModal emits save with { address, lat, lng }
const onLocationSaved = (payload) => {
  form.address = payload.address ?? form.address
  form.lat     = payload.lat     ?? form.lat
  form.lng     = payload.lng     ?? form.lng
  pickerOpen.value = false
}

const submit = () => {
  const payload = { office: { name: form.name, address: form.address, lat: form.lat, lng: form.lng } }
  if (props.office?.id) {
    form.transform(() => payload).patch(`/offices/${props.office.id}`, {
      preserveScroll: true,
      onSuccess: () => emit('close'),
    })
  } else {
    form.transform(() => payload).post('/offices', {
      preserveScroll: true,
      onSuccess: () => emit('close'),
    })
  }
}
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60"
    role="dialog"
    aria-modal="true"
    @click.self="emit('close')"
  >
    <div
      class="w-full h-full md:h-auto md:max-w-md md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]"
    >
      <header class="flex items-center justify-between p-4 border-b border-[color:var(--line)] flex-shrink-0">
        <h3 class="font-display text-base text-[color:var(--ink)]">
          {{ office?.id ? t('offices.edit') : t('offices.new') }}
        </h3>
        <button
          type="button"
          class="text-[color:var(--muted)] hover:text-[color:var(--ink)]"
          :aria-label="t('common.close')"
          @click="emit('close')"
        >
          <Icon
            name="x"
            :size="20"
          />
        </button>
      </header>

      <form
        id="office-form"
        class="flex-1 overflow-y-auto p-4 space-y-4"
        @submit.prevent="submit"
      >
        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('offices.fields.name') }} *
          </label>
          <UiInput
            v-model="form.name"
            type="text"
            required
          />
          <p
            v-if="form.errors.name"
            class="mt-1 text-xs text-[color:var(--danger)]"
          >
            {{ form.errors.name }}
          </p>
        </div>

        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('offices.fields.address') }}
          </label>
          <div class="flex items-center gap-2">
            <UiInput
              v-model="form.address"
              type="text"
              class="flex-1"
              :placeholder="t('offices.fields.addressPlaceholder')"
            />
            <UiButton
              type="button"
              variant="secondary"
              size="sm"
              @click="pickerOpen = true"
            >
              {{ t('offices.fields.pickOnMap') }}
            </UiButton>
          </div>
          <p
            v-if="form.lat != null && form.lng != null"
            class="mt-1 text-xs text-[color:var(--muted)]"
          >
            {{ Number(form.lat).toFixed(5) }}, {{ Number(form.lng).toFixed(5) }}
          </p>
        </div>
      </form>

      <footer class="flex items-center justify-end gap-2 p-4 border-t border-[color:var(--line)] flex-shrink-0 pb-[max(env(safe-area-inset-bottom),1rem)] md:pb-4">
        <UiButton
          variant="secondary"
          @click="emit('close')"
        >
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          type="submit"
          form="office-form"
          :disabled="!form.name || form.processing"
        >
          {{ t('common.save') }}
        </UiButton>
      </footer>
    </div>

    <LocationPickerModal
      :open="pickerOpen"
      :title="t('offices.fields.pickOnMap')"
      :address="form.address"
      :lat="form.lat"
      :lng="form.lng"
      @save="onLocationSaved"
      @close="pickerOpen = false"
    />
  </div>
</template>
